import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import ErrorState from "../../../components/common/ErrorState";
import { ListSkeleton } from "../../../components/common/Skeleton";
import { formatDate } from "../../../utils/formatters";

// 카테고리별 라벨 색상 (이벤트/점검/안내 등)
const CATEGORY_STYLES = {
  배송: "bg-blue-50 text-blue-700",
  상품: "bg-purple-50 text-purple-700",
  서비스: "bg-green-50 text-green-700",
  안내: "bg-gray-100 text-gray-700",
  운영: "bg-amber-50 text-amber-700",
};

// 7일 이내 작성된 공지는 NEW 뱃지 자동 표시
const isNewNotice = (createdAt) => {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - created <= sevenDays;
};

const NoticesSection = () => {
  const [notices, setNotices] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | error | success

  const loadNotices = useCallback(async () => {
    setStatus("loading");
    try {
      const response = await fetch("/mockdata/notices.json");
      if (!response.ok) throw new Error("Failed to fetch notices");
      const data = await response.json();
      // 최신순 정렬 후 상위 4개
      const sorted = [...data].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setNotices(sorted.slice(0, 4));
      setStatus("success");
    } catch (error) {
      console.error("공지사항을 불러오는데 실패했습니다:", error);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    loadNotices();
  }, [loadNotices]);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold text-charcoal">공지사항</h2>
          </div>
          <Link to="/support/notices">
            <Button variant="outline" size="sm">
              전체보기 <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        {status === "loading" && <ListSkeleton rows={4} />}

        {status === "error" && (
          <ErrorState
            title="공지사항을 불러오지 못했습니다"
            message="네트워크 상태를 확인한 뒤 다시 시도해주세요."
            onRetry={loadNotices}
          />
        )}

        {status === "success" && notices.length === 0 && (
          <Card className="bg-white border border-gray-200">
            <div className="p-8 text-center text-gray-500">
              등록된 공지사항이 없습니다.
            </div>
          </Card>
        )}

        {status === "success" && notices.length > 0 && (
          <Card className="bg-white border border-gray-200">
            <div className="divide-y divide-gray-200">
              {notices.map((notice) => (
                <Link
                  key={notice.id}
                  to={`/support/notices/${notice.id}`}
                  className="block hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                >
                  <div className="p-4 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {notice.category && (
                        <span
                          className={`shrink-0 text-xs px-2 py-1 rounded font-medium ${
                            CATEGORY_STYLES[notice.category] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {notice.category}
                        </span>
                      )}
                      {isNewNotice(notice.createdAt) && (
                        <span className="shrink-0 bg-rose-600 text-white text-xs px-2 py-1 rounded font-semibold">
                          NEW
                        </span>
                      )}
                      <span className="text-charcoal font-medium truncate">
                        {notice.title}
                      </span>
                    </div>
                    <span className="shrink-0 text-sm text-gray-500">
                      {formatDate(notice.createdAt, "yyyy.MM.dd")}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        )}
      </div>
    </section>
  );
};

export default NoticesSection;
